import { ConnectedPosition, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewContainerRef,
} from "@angular/core";

export type BvTooltipPosition = "above" | "below" | "before" | "after";

const POSITION_OFFSET = 8;

const POSITIONS: Record<BvTooltipPosition, ConnectedPosition> = {
  above: {
    originX: "center",
    originY: "top",
    overlayX: "center",
    overlayY: "bottom",
    offsetY: -POSITION_OFFSET,
  },
  below: {
    originX: "center",
    originY: "bottom",
    overlayX: "center",
    overlayY: "top",
    offsetY: POSITION_OFFSET,
  },
  before: {
    originX: "start",
    originY: "center",
    overlayX: "end",
    overlayY: "center",
    offsetX: -POSITION_OFFSET,
  },
  after: {
    originX: "end",
    originY: "center",
    overlayX: "start",
    overlayY: "center",
    offsetX: POSITION_OFFSET,
  },
};

/**
 * Internal container — what actually renders inside the overlay.
 */
@Component({
    selector: "bv-tooltip-container",
    // HTML mode renders sanitized markup (Angular strips anything unsafe; only
    // formatting tags like <br>/<strong> survive) so a single translated string
    // can carry line breaks — newlines don't survive the translation pipeline.
    template: `
      @if (html) {
        <div class="bv-tooltip__bubble bv-tooltip__bubble--html" [innerHTML]="message"></div>
      } @else {
        <div class="bv-tooltip__bubble">{{ message }}</div>
      }
    `,
    styles: [
        `
      :host {
        display: block;
      }

      .bv-tooltip__bubble {
        background: var(--bg-surface-alt);
        color: var(--text-primary);
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        line-height: var(--leading-snug);
        padding: var(--space-1-5) var(--space-2);
        border-radius: var(--radius-sm);
        border: 1px solid var(--bg-separator);
        box-shadow: var(--shadow-md);
        max-width: 240px;
        text-align: center;
        /* Honor explicit line breaks in multi-line messages; single-line
           tooltips (no newlines) are unaffected. */
        white-space: pre-line;
      }

      /* Multi-line HTML tooltips read better left-aligned than centered. */
      .bv-tooltip__bubble--html {
        text-align: left;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvTooltipContainerComponent {
  message = "";
  html = false;

  constructor(private cdr: ChangeDetectorRef) {}

  setMessage(m: string, html = false): void {
    this.message = m;
    this.html = html;
    this.cdr.markForCheck();
  }
}

/**
 * Hover/focus tooltip.
 *
 *   <button bv-button bvTooltip="Save changes">Save</button>
 *   <bv-icon name="info" bvTooltip="More info" bvTooltipPosition="below"></bv-icon>
 */
@Directive({ selector: "[bvTooltip]" })
export class BvTooltipDirective implements OnDestroy {
  @Input("bvTooltip") message = "";
  @Input("bvTooltipPosition") position: BvTooltipPosition = "above";
  /** Render the message as sanitized HTML (allows <br>, <strong>, …). */
  @Input("bvTooltipHtml") html = false;

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private vcr: ViewContainerRef
  ) {}

  @HostListener("mouseenter")
  @HostListener("focus")
  show(): void {
    if (!this.message || this.overlayRef) return;

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([POSITIONS[this.position]]),
      panelClass: "bv-tooltip-panel",
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new ComponentPortal(BvTooltipContainerComponent, this.vcr);
    const ref = this.overlayRef.attach(portal);
    ref.instance.setMessage(this.message, this.html);
  }

  @HostListener("mouseleave")
  @HostListener("blur")
  hide(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  ngOnDestroy(): void {
    this.hide();
  }
}
